import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PasswordInput from "@/components/PasswordInput";
import { DatePicker } from "@/components/DatePicker";
import { Plus, Download, Printer, Check, X, CalendarDays, LayoutList, FileText, BedDouble } from "lucide-react";
import { exportCsv, printView } from "@/lib/exportUtils";
import { exportBrandedPdf } from "@/lib/pdfExport";
import PlanningCalendar from "@/pages/acente/PlanningCalendar";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function isoToday() { return new Date().toISOString().slice(0, 10); }
function addDays(iso, n) {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

/* ---------------- Meal Planning ---------------- */
export function AcenteMealPlanning() {
  const [start, setStart] = useState(isoToday());
  const [end, setEnd] = useState(addDays(isoToday(), 60));
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("takvim"); // takvim | tablo
  const [metric, setMetric] = useState("total"); // total | adults | children | staff

  const load = () => {
    panelApi.get("/reports/meals", { params: { start, end } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [start, end]);

  // Total = adults + children + staff for each day.
  const enriched = useMemo(
    () => rows.map((r) => ({ ...r, total: (r.adults || 0) + (r.children || 0) + (r.staff || 0) })),
    [rows]
  );

  const totals = enriched.reduce(
    (acc, r) => ({ adults: acc.adults + r.adults, children: acc.children + r.children, staff: acc.staff + r.staff, total: acc.total + r.total }),
    { adults: 0, children: 0, staff: 0, total: 0 }
  );

  const metricLabels = {
    total: "Toplam Kişi",
    adults: "Yetişkin",
    children: "Çocuk",
    staff: "Görevli",
  };

  return (
    <div className="space-y-6" data-testid="acente-meal-planning">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Yemek Planlama</div>
          <h1 className="font-serif text-3xl text-brand-navy">Günlük Yemek Planı</h1>
          <p className="text-sm text-slate-600 mt-1">Takvimden hangi gün kaç yemek verildiğini tek bakışta görün.</p>
        </div>
        <ViewToggle view={view} setView={setView} />
      </header>

      <DateRange start={start} end={end} setStart={setStart} setEnd={setEnd} />

      {view === "takvim" && (
        <>
          <div className="flex flex-wrap gap-1 bg-canvas-stone p-1 rounded-full w-fit" data-testid="meal-metric-tabs">
            {Object.entries(metricLabels).map(([k, t]) => (
              <button
                key={k}
                onClick={() => setMetric(k)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm transition ${metric === k ? "bg-white shadow text-brand-navy font-medium" : "text-slate-600 hover:text-brand-navy"}`}
                data-testid={`meal-metric-${k}`}
              >
                {t}
              </button>
            ))}
          </div>
          <PlanningCalendar data={enriched} metric={metric} accent="orange" label={metricLabels[metric]} />
        </>
      )}

      {view === "tablo" && (
        <>
          <ExportToolbar
            rows={enriched}
            title="Yemek Planı"
            filename={`yemek-plani-${start}-${end}`}
            columns={[
              { key: "date", label: "Tarih" },
              { key: "adults", label: "Yetişkin" },
              { key: "children", label: "Çocuk" },
              { key: "staff", label: "Görevli" },
              { key: "total", label: "Toplam" },
            ]}
          />
          <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr className="text-left">
                  <th className="py-2 px-3">Tarih</th>
                  <th className="py-2 px-3">Yetişkin</th>
                  <th className="py-2 px-3">Çocuk</th>
                  <th className="py-2 px-3">Görevli</th>
                  <th className="py-2 px-3">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {enriched.map((r) => (
                  <tr key={r.date} className="border-b border-slate-100">
                    <td className="py-2 px-3 font-mono">{r.date}</td>
                    <td className="py-2 px-3 font-semibold">{r.adults}</td>
                    <td className="py-2 px-3">{r.children}</td>
                    <td className="py-2 px-3">{r.staff}</td>
                    <td className="py-2 px-3 font-semibold text-brand-orange">{r.total}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500">Kayıt yok</td></tr>}
              </tbody>
              <tfoot className="bg-canvas-stone">
                <tr>
                  <td className="py-2 px-3 font-medium">Toplam</td>
                  <td className="py-2 px-3 font-semibold">{totals.adults}</td>
                  <td className="py-2 px-3">{totals.children}</td>
                  <td className="py-2 px-3">{totals.staff}</td>
                  <td className="py-2 px-3 font-semibold text-brand-orange">{totals.total}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Room Planning ---------------- */
export function AcenteRoomPlanning() {
  const [start, setStart] = useState(isoToday());
  const [end, setEnd] = useState(addDays(isoToday(), 60));
  const [rows, setRows] = useState([]);
  const [view, setView] = useState("takvim");
  const [metric, setMetric] = useState("total"); // total | r1 | r2 | r3 | r4 | r5

  const load = () => {
    panelApi.get("/reports/rooms", { params: { start, end } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { load(); }, [start, end]);

  const metricLabels = { total: "Toplam Oda", r1: "1 Kişilik", r2: "2 Kişilik", r3: "3 Kişilik", r4: "4 Kişilik", r5: "5 Kişilik" };

  return (
    <div className="space-y-6" data-testid="acente-room-planning">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Oda Planlama</div>
          <h1 className="font-serif text-3xl text-brand-navy">Günlük Oda İhtiyacı</h1>
          <p className="text-sm text-slate-600 mt-1">Takvimden hangi gün kaç oda kullanıldığını renk yoğunluğuyla görün.</p>
        </div>
        <ViewToggle view={view} setView={setView} />
      </header>

      <DateRange start={start} end={end} setStart={setStart} setEnd={setEnd} />

      {view === "takvim" && (
        <>
          <div className="flex flex-wrap gap-1 bg-canvas-stone p-1 rounded-full w-fit" data-testid="room-metric-tabs">
            {Object.entries(metricLabels).map(([k, t]) => (
              <button
                key={k}
                onClick={() => setMetric(k)}
                className={`px-3 py-1 rounded-full text-xs sm:text-sm transition ${metric === k ? "bg-white shadow text-brand-navy font-medium" : "text-slate-600 hover:text-brand-navy"}`}
                data-testid={`room-metric-${k}`}
              >
                {t}
              </button>
            ))}
          </div>
          <PlanningCalendar data={rows} metric={metric} accent="navy" label={metricLabels[metric]} />
        </>
      )}

      {view === "tablo" && (
        <>
          <ExportToolbar
            rows={rows}
            title="Oda Planı"
            filename={`oda-plani-${start}-${end}`}
            columns={[
              { key: "date", label: "Tarih" },
              { key: "r1", label: "1K" },
              { key: "r2", label: "2K" },
              { key: "r3", label: "3K" },
              { key: "r4", label: "4K" },
              { key: "r5", label: "5K" },
              { key: "total", label: "Toplam" },
            ]}
          />
          <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr className="text-left">
                  <th className="py-2 px-3">Tarih</th>
                  <th className="py-2 px-3">1K</th>
                  <th className="py-2 px-3">2K</th>
                  <th className="py-2 px-3">3K</th>
                  <th className="py-2 px-3">4K</th>
                  <th className="py-2 px-3">5K</th>
                  <th className="py-2 px-3">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.date} className="border-b border-slate-100">
                    <td className="py-2 px-3 font-mono">{r.date}</td>
                    <td className="py-2 px-3">{r.r1}</td>
                    <td className="py-2 px-3">{r.r2}</td>
                    <td className="py-2 px-3">{r.r3}</td>
                    <td className="py-2 px-3">{r.r4}</td>
                    <td className="py-2 px-3">{r.r5}</td>
                    <td className="py-2 px-3 font-semibold">{r.total}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Kayıt yok</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Reports (summary + downloadable) ---------------- */
/* ---------------- Reports (rich) ---------------- */
export function AcenteReports() {
  const today = new Date();
  const in30 = new Date(today.getTime() + 30 * 86400 * 1000);
  const isoOf = (d) => d.toISOString().slice(0, 10);

  const [summary, setSummary] = useState(null);
  const [start, setStart] = useState(isoOf(today));
  const [end, setEnd] = useState(isoOf(in30));
  const [meals, setMeals] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    panelApi.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const loadRange = () => {
    setBusy(true);
    Promise.all([
      panelApi.get(`/reports/meals`, { params: { start, end } }).catch(() => ({ data: [] })),
      panelApi.get(`/reports/rooms`, { params: { start, end } }).catch(() => ({ data: [] })),
    ]).then(([m, r]) => {
      setMeals(m.data || []);
      setRooms(r.data || []);
    }).finally(() => setBusy(false));
  };

  useEffect(() => { loadRange(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = [
    { key: "today_checkin",  label: "Bugün Girişler",    tone: "from-emerald-500 to-emerald-700", link: "/acente/bugun-girisler" },
    { key: "today_checkout", label: "Bugün Çıkışlar",    tone: "from-rose-500 to-rose-700",       link: "/acente/rezervasyonlar" },
    { key: "pending",        label: "Onay Bekleyen",     tone: "from-amber-500 to-amber-700",     link: "/acente/rezervasyonlar?durum=onay_bekleyen" },
    { key: "missing_info",   label: "Eksik Bilgi",       tone: "from-orange-500 to-orange-700",   link: "/acente/rezervasyonlar?durum=eksik_bilgi" },
    { key: "approved",       label: "Onaylı",            tone: "from-brand-navy to-brand-navy",   link: "/acente/rezervasyonlar?durum=onaylandi" },
    { key: "total",          label: "Toplam",            tone: "from-brand-orange to-brand-orange-dark", link: "/acente/rezervasyonlar" },
  ];

  // Simple aggregate rows (meals: kahvalti/aksam/…, rooms: r1..r5) for date range
  const mealTotals = (meals || []).reduce((a, m) => ({
    kahvalti: a.kahvalti + (m.kahvalti || 0),
    ogle:     a.ogle     + (m.ogle     || 0),
    aksam:    a.aksam    + (m.aksam    || 0),
  }), { kahvalti: 0, ogle: 0, aksam: 0 });
  const roomTotals = (rooms || []).reduce((a, r) => ({
    r1: a.r1 + (r.r1 || 0), r2: a.r2 + (r.r2 || 0), r3: a.r3 + (r.r3 || 0), r4: a.r4 + (r.r4 || 0), r5: a.r5 + (r.r5 || 0),
  }), { r1: 0, r2: 0, r3: 0, r4: 0, r5: 0 });

  return (
    <div className="space-y-6" data-testid="acente-reports">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Raporlar</div>
        <h1 className="font-serif text-3xl text-brand-navy">Genel Raporlar</h1>
        <p className="text-sm text-slate-600 mt-1">Anlık özet + istediğiniz tarih aralığı için yemek ve oda dağılımı.</p>
      </header>

      {/* KPI summary cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6" data-testid="reports-summary-cards">
        {summary && cards.map((c) => (
          <Link
            key={c.key}
            to={c.link}
            className={`rounded-2xl p-4 text-white bg-gradient-to-br ${c.tone} shadow-sm hover:shadow-md hover:-translate-y-0.5 transition block`}
            data-testid={`reports-card-${c.key}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-white/85">{c.label}</div>
            <div className="font-serif text-3xl mt-1">{summary[c.key] ?? 0}</div>
          </Link>
        ))}
      </div>

      {/* Date range picker */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-4 flex flex-wrap items-end gap-3" data-testid="reports-range-picker">
        <div>
          <Label>Başlangıç</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="reports-range-start" />
        </div>
        <div>
          <Label>Bitiş</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="reports-range-end" />
        </div>
        <Button onClick={loadRange} disabled={busy} className="bg-brand-orange text-white" data-testid="reports-load">
          {busy ? "Yükleniyor…" : "Raporu Yenile"}
        </Button>
      </div>

      {/* Meal report */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="reports-meals-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-brand-navy">Öğün Bazında Kişi Sayıları</h3>
          <div className="text-xs text-slate-500">
            Toplam: <b>Kahvaltı</b> {mealTotals.kahvalti} · <b>Öğle</b> {mealTotals.ogle} · <b>Akşam</b> {mealTotals.aksam}
          </div>
        </div>
        {meals.length === 0 ? (
          <div className="text-center text-sm text-slate-500 p-6">Bu tarih aralığında öğün verisi yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-canvas-stone">
                <tr>
                  <th className="text-left py-2 px-3">Tarih</th>
                  <th className="text-right py-2 px-3">Kahvaltı</th>
                  <th className="text-right py-2 px-3">Öğle</th>
                  <th className="text-right py-2 px-3">Akşam</th>
                  <th className="text-right py-2 px-3">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {meals.map((m) => (
                  <tr key={m.date} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-mono text-xs">{m.date}</td>
                    <td className="py-2 px-3 text-right">{m.kahvalti || 0}</td>
                    <td className="py-2 px-3 text-right">{m.ogle || 0}</td>
                    <td className="py-2 px-3 text-right">{m.aksam || 0}</td>
                    <td className="py-2 px-3 text-right font-semibold text-brand-orange">
                      {(m.kahvalti || 0) + (m.ogle || 0) + (m.aksam || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Room report */}
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid="reports-rooms-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-brand-navy">Oda Tipi Bazında Doluluk</h3>
          <div className="text-xs text-slate-500">
            Toplam: 1K {roomTotals.r1} · 2K {roomTotals.r2} · 3K {roomTotals.r3} · 4K {roomTotals.r4} · 5K {roomTotals.r5}
          </div>
        </div>
        {rooms.length === 0 ? (
          <div className="text-center text-sm text-slate-500 p-6">Bu tarih aralığında oda verisi yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-canvas-stone">
                <tr>
                  <th className="text-left py-2 px-3">Tarih</th>
                  <th className="text-right py-2 px-3">1K</th>
                  <th className="text-right py-2 px-3">2K</th>
                  <th className="text-right py-2 px-3">3K</th>
                  <th className="text-right py-2 px-3">4K</th>
                  <th className="text-right py-2 px-3">5K</th>
                  <th className="text-right py-2 px-3">Toplam Oda</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((r) => (
                  <tr key={r.date} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-mono text-xs">{r.date}</td>
                    <td className="py-2 px-3 text-right">{r.r1 || 0}</td>
                    <td className="py-2 px-3 text-right">{r.r2 || 0}</td>
                    <td className="py-2 px-3 text-right">{r.r3 || 0}</td>
                    <td className="py-2 px-3 text-right">{r.r4 || 0}</td>
                    <td className="py-2 px-3 text-right">{r.r5 || 0}</td>
                    <td className="py-2 px-3 text-right font-semibold text-brand-navy">
                      {(r.r1||0)+(r.r2||0)+(r.r3||0)+(r.r4||0)+(r.r5||0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Agencies (admin) ---------------- */
export function AcenteAgencies() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", contact: "", phone: "", tax_id: "", active: true });
  const [deleteTarget, setDeleteTarget] = useState(null); // {agency, cascade, busy}
  const load = () => panelApi.get("/agencies").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await panelApi.post("/agencies", form);
      toast.success("Acente eklendi");
      setForm({ name: "", contact: "", phone: "", tax_id: "", active: true });
      load();
    } catch (err) {
      toast.error(err?.friendly || err?.response?.data?.detail || "Eklenemedi");
    }
  };

  const toggleActive = async (a) => {
    try {
      await panelApi.patch(`/agencies/${a.id}/active?active=${!a.active}`);
      load();
    } catch (err) { toast.error(err?.friendly || err?.response?.data?.detail || "Güncellenemedi"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { agency, cascade } = deleteTarget;
    setDeleteTarget({ ...deleteTarget, busy: true });
    try {
      // cascade=true tells the backend to also HARD-delete every reservation
      // (+ their revisions/invoices/change-logs) belonging to this agency.
      await panelApi.delete(`/agencies/${agency.id}${cascade ? "?cascade=true" : ""}`);
      toast.success(cascade
        ? `${agency.name} arşive taşındı — rezervasyonları da arşivlendi (Silinen Şirketler'den geri getirebilirsiniz)`
        : `${agency.name} arşive taşındı`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.friendly || err?.response?.data?.detail || "Silinemedi");
      setDeleteTarget((s) => (s ? { ...s, busy: false } : s));
    }
  };

  return (
    <div className="space-y-6" data-testid="acente-agencies">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Acente Yönetimi</div>
          <h1 className="font-serif text-3xl text-brand-navy">Acenteler</h1>
        </div>
        <a
          href="/acente/silinen-sirketler"
          className="text-xs px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 inline-flex items-center gap-1"
          data-testid="agencies-deleted-link"
        >
          Silinen Şirketler →
        </a>
      </header>
      <div className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-navy/10 p-5 grid gap-3" data-testid="agency-form">
          <h3 className="font-serif text-lg text-brand-navy">Yeni Acente</h3>
          <div><Label>Ad *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="agency-name" /></div>
          <div><Label>İletişim Kişisi</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="agency-contact" /></div>
          <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="agency-phone" /></div>
          <div><Label>Vergi No</Label><Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} data-testid="agency-tax" /></div>
          <Button type="submit" className="bg-brand-orange text-white" data-testid="agency-submit"><Plus size={14} className="mr-1"/> Ekle</Button>
        </form>
        <div className="lg:col-span-2 space-y-2" data-testid="agency-list">
          {items.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-brand-navy/10 p-4 flex items-center justify-between gap-3" data-testid={`agency-row-${a.id}`}>
              <Link to={`/acente/acenteler/${a.id}`} className="flex-1 min-w-0 group" data-testid={`agency-open-${a.id}`}>
                <div className="font-medium text-brand-navy group-hover:text-brand-orange">{a.name}</div>
                <div className="text-xs text-slate-500">{a.contact} · {a.phone}</div>
                <div className="text-[10px] text-brand-orange/70 mt-1 group-hover:text-brand-orange">Detay için tıklayın →</div>
              </Link>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleActive(a)} className={`text-xs px-3 py-1 rounded-full ${a.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid={`agency-toggle-${a.id}`}>
                  {a.active ? "Aktif" : "Pasif"}
                </button>
                <button onClick={() => setDeleteTarget({ agency: a, cascade: false, busy: false })} className="text-xs px-3 py-1 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200" data-testid={`agency-delete-${a.id}`}>Sil</button>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-slate-500 text-sm">Kayıt yok.</div>}
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="agency-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Acente Sil: {deleteTarget?.agency?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              Bu acente <b>Silinen Şirketler</b> arşivine taşınacak. Rezervasyonu olan acenteler için
              aşağıdaki seçeneği işaretlemeniz gerekir. Dilerseniz sonradan
              tek tıkla geri getirebilirsiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-3 rounded-xl border-2 border-rose-200 bg-rose-50 p-3 cursor-pointer" data-testid="agency-cascade-toggle">
            <input
              type="checkbox"
              checked={!!deleteTarget?.cascade}
              onChange={(e) => setDeleteTarget((s) => (s ? { ...s, cascade: e.target.checked } : s))}
              className="w-5 h-5 mt-0.5 rounded border-rose-300 text-rose-600 focus:ring-rose-500 cursor-pointer flex-shrink-0"
              data-testid="agency-cascade-checkbox"
            />
            <div className="text-sm text-rose-900">
              <div className="font-semibold">Bu acentenin tüm rezervasyonlarını da arşivle</div>
              <div className="text-xs text-rose-800/80 mt-0.5">
                İşaretlerseniz, acenteye ait rezervasyonlar (revizyon ve fatura talepleri dahil) <b>Silinen Şirketler</b> arşivine taşınır — kalıcı silme yoktur, gerekince geri getirebilirsiniz.
              </div>
            </div>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="agency-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={!!deleteTarget?.busy}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="agency-delete-confirm"
            >
              {deleteTarget?.busy ? "Arşivleniyor..." : (deleteTarget?.cascade ? "Arşive Taşı (Acente + Rezervasyonlar)" : "Arşive Taşı")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Users (admin) ---------------- */
export function AcenteUsers() {
  // Note: Acente users are managed via /acente/acenteler and self-signup, so
  // this page is scoped to STAFF accounts + özel acente (B2C) + saha çalışanı.
  const ROLES = [
    { v: "operasyon", t: "Operasyon" },
    { v: "admin", t: "Yönetici" },
    { v: "ozel_acente", t: "Özel Acente (B2C)" },
    { v: "saha_calisani", t: "Saha Çalışanı (Otel)" },
  ];
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "operasyon", agency_id: "", hotel_names: [] });
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const load = () => {
    panelApi.get("/users").then((r) => {
      // Hide agency (acente) accounts — they belong under Acenteler.
      const filtered = (r.data || []).filter((u) => u.role !== "acente");
      setItems(filtered);
    }).catch(() => {});
    panelApi.get("/agencies").then((r) => setAgencies(r.data)).catch(() => {});
    panelApi.get("/hotels").then((r) => setHotels(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Explicit guard: role=ozel_acente must be tied to an agency. Backend
    // auto-creates one when agency_id is blank, but nudging admins to pick
    // an existing agency avoids silent duplicates when they meant to reuse one.
    if (form.role === "ozel_acente" && !form.agency_id) {
      const goAuto = window.confirm(
        `"${form.name || form.email}" için otomatik bir Özel Acente kaydı oluşturulacak. Devam edilsin mi?\n\n(Mevcut bir acenteye bağlamak istiyorsanız İptal edip aşağıdan seçin.)`
      );
      if (!goAuto) return;
    }
    if (form.role === "saha_calisani" && (form.hotel_names || []).length === 0) {
      return toast.error("Saha çalışanı için en az bir otel seçmelisiniz");
    }
    try {
      const r = await panelApi.post("/users", {
        email: form.email,
        password: form.password,
        name: form.name,
        role: form.role,
        agency_id: form.agency_id || null,
        hotel_names: form.role === "saha_calisani" ? form.hotel_names : null,
      });
      toast.success("Kullanıcı eklendi");
      setForm({ email: "", password: "", name: "", role: "operasyon", agency_id: "", hotel_names: [] });
      load();
      // Refresh agency list so the newly auto-created record appears next time.
      if (r?.data?.agency_id && !form.agency_id) {
        panelApi.get("/agencies").then((rr) => setAgencies(rr.data)).catch(() => {});
      }
    } catch (err) {
      toast.error(err?.friendly || err?.response?.data?.detail || "Eklenemedi");
    }
  };

  // Quick inline "+ Yeni Acente" — creates an agency named after whatever the
  // admin already typed as the user's name (or a prompt if the name is blank)
  // and immediately selects it in the dropdown. Avoids the "leave this page,
  // create an agency, come back" ping-pong.
  const createInlineAgency = async () => {
    const seedName = (form.name || "").trim();
    const name = seedName || window.prompt("Yeni acentenin adı:");
    if (!name || !name.trim()) return;
    try {
      const r = await panelApi.post("/agencies", { name: name.trim(), contact: "", phone: "", tax_id: "", active: true });
      // Re-fetch so the dropdown shows the fresh row, then select it.
      const list = await panelApi.get("/agencies");
      setAgencies(list.data);
      setForm((f) => ({ ...f, agency_id: r?.data?.id }));
      toast.success(`Acente oluşturuldu: ${name.trim()}`);
    } catch (err) {
      toast.error(err?.friendly || err?.response?.data?.detail || "Acente oluşturulamadı");
    }
  };

  const setActive = async (u, active) => {
    try { await panelApi.patch(`/users/${u.id}/active?active=${active}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Güncellenemedi"); }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    const initialHotels = Array.isArray(u.hotel_names) && u.hotel_names.length > 0
      ? [...u.hotel_names]
      : (u.hotel_name ? [u.hotel_name] : []);
    setEditRow({ name: u.name || "", role: u.role, agency_id: u.agency_id || "", hotel_names: initialHotels, password: "" });
  };

  const saveEdit = async (uid) => {
    try {
      const payload = { name: editRow.name, role: editRow.role, agency_id: editRow.agency_id || null };
      if (editRow.role === "saha_calisani") {
        if (!(editRow.hotel_names || []).length) return toast.error("Saha çalışanı için en az bir otel seçmelisiniz");
        payload.hotel_names = editRow.hotel_names;
      }
      if (editRow.password && editRow.password.length >= 6) payload.password = editRow.password;
      await panelApi.patch(`/users/${uid}`, payload);
      toast.success("Kullanıcı güncellendi");
      setEditingId(null); setEditRow({});
      load();
    } catch (err) { toast.error(err?.friendly || err?.response?.data?.detail || "Güncellenemedi"); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısını KALICI olarak silmek istiyor musunuz?`)) return;
    try {
      await panelApi.delete(`/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi"); }
  };

  const isLegacy = (u) => ["rezervasyon", "yemek"].includes(u.role);

  return (
    <div className="space-y-6" data-testid="acente-users">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Kullanıcı Yönetimi</div>
        <h1 className="font-serif text-3xl text-brand-navy">Kullanıcılar</h1>
        <p className="text-sm text-slate-600 mt-1">Yönetici ve Operasyon personelini buradan yönetin. Acente hesapları <a href="/acente/acenteler" className="text-brand-orange hover:underline">Acenteler</a> sayfasından yönetilir.</p>
      </header>
      <div className="grid gap-6 lg:grid-cols-3">
        <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-navy/10 p-5 grid gap-3" data-testid="user-form">
          <h3 className="font-serif text-lg text-brand-navy">Yeni Kullanıcı</h3>
          <div><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name" /></div>
          <div><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email" /></div>
          <div><Label>Şifre (min. 6)</Label><PasswordInput value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-password" /></div>
          <div>
            <Label>Rol</Label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="user-role">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.t}</option>)}
            </select>
          </div>
          {(form.role === "acente" || form.role === "ozel_acente") && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="user-agency-block">
              <Label className="text-emerald-900">
                Bağlı Acente {form.role === "ozel_acente" && <span className="text-slate-500 text-xs">(boş bırakırsanız otomatik oluşturulur)</span>}
              </Label>
              <div className="flex gap-2">
                <select
                  value={form.agency_id}
                  onChange={(e) => setForm({ ...form, agency_id: e.target.value })}
                  className="flex h-10 flex-1 rounded-md border border-input bg-white px-3 py-2 text-sm"
                  data-testid="user-agency"
                >
                  <option value="">— {form.role === "ozel_acente" ? "Otomatik oluştur" : "Seçin"} —</option>
                  {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={createInlineAgency}
                  className="rounded-md bg-emerald-600 text-white text-xs px-3 py-2 hover:bg-emerald-700 whitespace-nowrap"
                  data-testid="user-agency-inline-create"
                  title="Yeni acente oluştur ve seç"
                >
                  <Plus size={12} className="inline -mt-0.5 mr-1"/> Yeni
                </button>
              </div>
              {form.role === "ozel_acente" && (
                <p className="text-[11px] text-emerald-800/80">
                  Özel acente hesabı bir acente kaydına bağlı olmalıdır. Boş bırakırsanız
                  <b> "{form.name || 'kullanıcı'}"</b> adıyla otomatik bir acente oluşturulur.
                </p>
              )}
            </div>
          )}
          {form.role === "saha_calisani" && (
            <div className="rounded-xl border-2 border-brand-orange/30 bg-brand-orange/5 p-3 space-y-2" data-testid="user-hotel-block">
              <Label className="text-brand-orange-dark inline-flex items-center gap-1">
                <BedDouble size={12} /> Atanan Oteller *
                <span className="text-[10px] text-slate-500 ml-auto font-normal">
                  {(form.hotel_names || []).length} seçili
                </span>
              </Label>
              <HotelMultiSelect
                hotels={hotels}
                value={form.hotel_names || []}
                onChange={(v) => setForm({ ...form, hotel_names: v })}
                testid="user-hotel-multi"
              />
              <p className="text-[11px] text-brand-orange-dark/80">
                Saha çalışanı seçtiğiniz otellere yapılacak 3 günlük girişleri görür. Birden fazla otel seçebilirsiniz.
              </p>
            </div>
          )}
          <Button type="submit" className="bg-brand-orange text-white" data-testid="user-submit"><Plus size={14} className="mr-1"/> Ekle</Button>
        </form>
        <div className="lg:col-span-2 space-y-2" data-testid="user-list">
          {items.map((u) => {
            const isEditing = editingId === u.id;
            return (
              <div
                key={u.id}
                className={`bg-white rounded-2xl border p-4 ${isLegacy(u) ? "border-orange-300 bg-orange-50/60" : "border-brand-navy/10"}`}
                data-testid={`user-row-${u.id}`}
              >
                {!isEditing ? (
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-brand-navy truncate">
                        {u.name || "-"} <span className="text-xs text-slate-500 font-normal">· {u.email}</span>
                      </div>
                      <div className="text-xs uppercase mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="text-slate-500">{u.role}</span>
                        {u.role === "saha_calisani" && (Array.isArray(u.hotel_names) ? u.hotel_names : (u.hotel_name ? [u.hotel_name] : [])).map((hn) => (
                          <span key={hn} className="rounded-full bg-brand-orange/10 text-brand-orange-dark px-2 py-0.5 text-[10px] inline-flex items-center gap-1">
                            <BedDouble size={10} /> {hn}
                          </span>
                        ))}
                        {isLegacy(u) && <span className="rounded-full bg-orange-200 text-orange-800 px-2 py-0.5 text-[10px]">Eski rol — güncelleyin</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => startEdit(u)} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`user-edit-${u.id}`}>Düzenle</button>
                      <button onClick={() => setActive(u, !u.active)} className={`text-xs px-3 py-1 rounded-full ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`} data-testid={`user-toggle-${u.id}`}>
                        {u.active ? "Aktif" : "Pasif"}
                      </button>
                      <button onClick={() => deleteUser(u)} className="text-xs px-3 py-1 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200" data-testid={`user-delete-${u.id}`}>Sil</button>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div><Label>Ad Soyad</Label><Input value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} /></div>
                    <div>
                      <Label>Rol</Label>
                      <select value={editRow.role} onChange={(e) => setEditRow({ ...editRow, role: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid={`user-edit-role-${u.id}`}>
                        {ROLES.map((r) => <option key={r.v} value={r.v}>{r.t}</option>)}
                      </select>
                    </div>
                    {(editRow.role === "acente" || editRow.role === "ozel_acente") && (
                      <div className="md:col-span-2">
                        <Label>Bağlı Acente</Label>
                        <select value={editRow.agency_id || ""} onChange={(e) => setEditRow({ ...editRow, agency_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm">
                          <option value="">— Seçin —</option>
                          {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                      </div>
                    )}
                    {editRow.role === "saha_calisani" && (
                      <div className="md:col-span-2 rounded-xl border-2 border-brand-orange/30 bg-brand-orange/5 p-3 space-y-2">
                        <Label className="text-brand-orange-dark inline-flex items-center gap-1">
                          <BedDouble size={12} /> Atanan Oteller *
                          <span className="text-[10px] text-slate-500 ml-auto font-normal">
                            {(editRow.hotel_names || []).length} seçili
                          </span>
                        </Label>
                        <HotelMultiSelect
                          hotels={hotels}
                          value={editRow.hotel_names || []}
                          onChange={(v) => setEditRow({ ...editRow, hotel_names: v })}
                          testid={`user-edit-hotels-${u.id}`}
                        />
                        <p className="text-[11px] text-brand-orange-dark/80">
                          En az bir otel seçilmelidir. Birden fazla otel seçildiğinde saha panelinde tümü karışık görüntülenir.
                        </p>
                      </div>
                    )}
                    <div className="md:col-span-2"><Label>Yeni Şifre (isteğe bağlı, min. 6)</Label><PasswordInput value={editRow.password} onChange={(e) => setEditRow({ ...editRow, password: e.target.value })} placeholder="Boş bırakılırsa değişmez" data-testid="user-edit-password" /></div>
                    <div className="md:col-span-2 flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => { setEditingId(null); setEditRow({}); }}>Vazgeç</Button>
                      <Button onClick={() => saveEdit(u.id)} className="bg-brand-orange text-white" data-testid={`user-save-${u.id}`}>Kaydet</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && <div className="text-slate-500 text-sm">Kayıt yok.</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Logs (admin) ---------------- */
export function AcenteLogs() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { panelApi.get("/logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="acente-logs">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Sistem</div>
        <h1 className="font-serif text-3xl text-brand-navy">Değişiklik Kayıtları</h1>
      </header>
      <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
            <tr className="text-left">
              <th className="py-2 px-3">Zaman</th>
              <th className="py-2 px-3">Kullanıcı</th>
              <th className="py-2 px-3">İşlem</th>
              <th className="py-2 px-3">Alan</th>
              <th className="py-2 px-3">Eski</th>
              <th className="py-2 px-3">Yeni</th>
              <th className="py-2 px-3">Rez.</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-b border-slate-100" data-testid={`log-row-${l.id}`}>
                <td className="py-2 px-3 font-mono text-xs">{l.timestamp?.slice(0, 19).replace("T", " ")}</td>
                <td className="py-2 px-3 text-xs">{l.user_email}</td>
                <td className="py-2 px-3 text-xs">{l.action}</td>
                <td className="py-2 px-3 text-xs">{l.field}</td>
                <td className="py-2 px-3 text-xs text-slate-500 max-w-[180px] truncate">{l.old_value}</td>
                <td className="py-2 px-3 text-xs text-brand-navy max-w-[180px] truncate">{l.new_value}</td>
                <td className="py-2 px-3 font-mono text-[10px] text-slate-400">{l.reservation_id?.slice(0, 8)}…</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Kayıt yok</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DateRange({ start, end, setStart, setEnd }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-navy/10 p-4 flex flex-wrap items-end gap-3">
      <div className="min-w-[220px]">
        <Label>Başlangıç</Label>
        <DatePicker value={start} onChange={(v) => setStart(v)} placeholder="Başlangıç seçin" data-testid="report-date-start" />
      </div>
      <div className="min-w-[220px]">
        <Label>Bitiş</Label>
        <DatePicker value={end} onChange={(v) => setEnd(v)} minDate={start || undefined} placeholder="Bitiş seçin" data-testid="report-date-end" />
      </div>
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div className="inline-flex bg-canvas-stone p-1 rounded-full" data-testid="planning-view-toggle">
      <button
        onClick={() => setView("takvim")}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm transition ${
          view === "takvim" ? "bg-white shadow text-brand-navy font-medium" : "text-slate-600 hover:text-brand-navy"
        }`}
        data-testid="view-takvim"
      >
        <CalendarDays size={14}/> Takvim
      </button>
      <button
        onClick={() => setView("tablo")}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm transition ${
          view === "tablo" ? "bg-white shadow text-brand-navy font-medium" : "text-slate-600 hover:text-brand-navy"
        }`}
        data-testid="view-tablo"
      >
        <LayoutList size={14}/> Tablo
      </button>
    </div>
  );
}

function ExportToolbar({ rows, columns, filename, title }) {
  return (
    <div className="flex flex-wrap gap-2 justify-end">
      <button
        onClick={() => exportCsv(filename, columns, rows)}
        disabled={!rows?.length}
        className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/20 px-3 py-1.5 text-xs hover:bg-brand-navy/5 disabled:opacity-40"
        data-testid={`export-csv-${filename}`}
      >
        <Download size={12}/> CSV / Excel
      </button>
      <button
        onClick={() => printView(title, columns, rows)}
        disabled={!rows?.length}
        className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/20 px-3 py-1.5 text-xs hover:bg-brand-navy/5 disabled:opacity-40"
        data-testid={`export-print-${filename}`}
      >
        <Printer size={12}/> Yazdır
      </button>
      <button
        onClick={() => exportBrandedPdf({
          title,
          columns,
          rows,
          meta: { "Toplam Kayıt": rows.length, "Tarih": new Date().toLocaleDateString("tr-TR") },
          footerNote: "Ariaf ASIS Acente Paneli · rapor çıktısı",
        })}
        disabled={!rows?.length}
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-navy to-brand-navy/90 text-white px-3 py-1.5 text-xs hover:from-brand-orange-dark hover:to-brand-orange disabled:opacity-40"
        data-testid={`export-pdf-${filename}`}
      >
        <FileText size={12}/> PDF Oluştur
      </button>
    </div>
  );
}

/* ---------------- Revision Requests ---------------- */
export function AcenteRevisions() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("beklemede");
  const [resolvingId, setResolvingId] = useState(null);
  const [note, setNote] = useState("");

  const load = () => panelApi.get("/revisions", { params: { status: tab || undefined } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [tab]);

  const resolve = async (id, action) => {
    try {
      await panelApi.patch(`/revisions/${id}/resolve`, { action, ops_note: note });
      toast.success(action === "onayla" ? "Talep onaylandı ve değişiklikler uygulandı" : "Talep reddedildi");
      setResolvingId(null);
      setNote("");
      load();
    } catch (err) {
      toast.error(err?.friendly || err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <div className="space-y-6" data-testid="acente-revisions-page">
      <header>
        <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Revizyon Talepleri</div>
        <h1 className="font-serif text-3xl text-brand-navy">Rezervasyon Değişiklik Talepleri</h1>
        <p className="text-sm text-slate-600 mt-1">Acentelerin çıkış tarihi geçmemiş rezervasyonlar üzerinde talep ettiği değişiklikler. Onaylandığında rezervasyona uygulanır.</p>
      </header>

      <div className="inline-flex bg-canvas-stone p-1 rounded-full" data-testid="revisions-tabs">
        {["beklemede", "onaylandi", "reddedildi"].map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={`px-4 py-1.5 rounded-full text-sm ${tab === s ? "bg-white shadow text-brand-navy" : "text-slate-600"}`}
            data-testid={`revisions-tab-${s}`}
          >
            {{ beklemede: "Bekleyenler", onaylandi: "Onaylı", reddedildi: "Reddedilmiş" }[s]}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="bg-white rounded-2xl border border-dashed border-brand-gold/40 p-6 text-sm text-slate-500">Bu kategoride talep yok.</div>}
        {items.map((it) => (
          <div key={it.id} className="bg-white rounded-2xl border border-brand-navy/10 p-5" data-testid={`revision-item-${it.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs font-mono text-slate-500">{it.reservation_number}</div>
                <div className="text-sm text-brand-navy font-medium mt-1">{it.requested_by_email}</div>
                <div className="text-xs text-slate-400">{it.created_at?.slice(0, 19).replace("T", " ")}</div>
              </div>
              <div className={`text-xs px-3 py-1 rounded-full ${
                it.status === "beklemede" ? "bg-amber-100 text-amber-700" :
                it.status === "onaylandi" ? "bg-emerald-100 text-emerald-700" :
                "bg-rose-100 text-rose-700"
              }`}>{{ beklemede: "Bekliyor", onaylandi: "Onaylandı", reddedildi: "Reddedildi" }[it.status]}</div>
            </div>

            <div className="mt-3 rounded-lg bg-canvas-stone p-3 text-sm text-slate-700 whitespace-pre-wrap">{it.note}</div>

            {Object.keys(it.proposed_changes || {}).length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Önerilen Değişiklikler ({Object.keys(it.proposed_changes).length})</div>
                <div className="bg-brand-orange/5 border border-brand-orange/30 rounded-lg p-3 space-y-1">
                  {Object.entries(it.proposed_changes).map(([k, v]) => {
                    const label = ({
                      group_name: "Grup Adı", checkin: "Giriş", checkout: "Çıkış",
                      adults: "Yetişkin", children: "Çocuk", staff: "Görevli",
                      meal_start: "Başlangıç Öğünü", meal_end: "Bitiş Öğünü", notes: "Not",
                      rooms: "Oda Dağılımı",
                    }[k]) || k;
                    let display;
                    if (k === "rooms" && v && typeof v === "object") {
                      display = ["r1", "r2", "r3", "r4", "r5"].map((rk, i) => `${i + 1}K:${v[rk] ?? 0}`).join(" · ");
                    } else {
                      display = String(v);
                    }
                    return (
                      <div key={k} className="flex items-start gap-3 text-sm">
                        <span className="text-xs uppercase tracking-widest text-brand-orange w-32 flex-shrink-0">{label}</span>
                        <span className="text-brand-navy font-medium">{display}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {it.status === "beklemede" && (
              <div className="mt-4 border-t pt-4 space-y-2">
                {resolvingId === it.id ? (
                  <>
                    <Input placeholder="Kısa not (opsiyonel)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`revision-note-${it.id}`} />
                    <div className="flex gap-2">
                      <Button onClick={() => resolve(it.id, "onayla")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`revision-approve-${it.id}`}>
                        <Check size={14} className="mr-1"/> Onayla ve Uygula
                      </Button>
                      <Button onClick={() => resolve(it.id, "reddet")} variant="outline" className="text-rose-600" data-testid={`revision-reject-${it.id}`}>
                        <X size={14} className="mr-1"/> Reddet
                      </Button>
                      <Button variant="ghost" onClick={() => { setResolvingId(null); setNote(""); }}>Vazgeç</Button>
                    </div>
                  </>
                ) : (
                  <Button onClick={() => setResolvingId(it.id)} className="bg-brand-navy text-white" data-testid={`revision-resolve-${it.id}`}>Değerlendir</Button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * HotelMultiSelect — chip-based multi-select for assigning saha_calisani
 * users to one or more hotels. Selected items pin to the top with a
 * remove ×; the rest are searchable via a filter input. Kept in this file
 * (rather than /components) because it's the only consumer for now.
 */
function HotelMultiSelect({ hotels = [], value = [], onChange, testid = "hotel-multi" }) {
  const [q, setQ] = React.useState("");
  const selected = new Set(value);
  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return hotels.filter((h) => {
      const name = (h.name || h).toString();
      if (!name) return false;
      return !needle || name.toLowerCase().includes(needle);
    });
  }, [hotels, q]);

  const toggle = (name) => {
    if (selected.has(name)) {
      onChange(value.filter((v) => v !== name));
    } else {
      onChange([...value, name]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <div data-testid={testid}>
      {/* Selected chips — show at the top so the admin sees the current
          selection at a glance without scrolling. */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`${testid}-selected`}>
          {value.map((n) => (
            <span
              key={n}
              className="inline-flex items-center gap-1 rounded-full bg-brand-orange text-white text-[11px] px-2.5 py-1"
            >
              {n}
              <button
                type="button"
                onClick={() => toggle(n)}
                className="ml-1 hover:bg-white/20 rounded-full w-4 h-4 flex items-center justify-center"
                aria-label={`${n} - kaldır`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-[10px] text-slate-500 hover:text-rose-600 underline decoration-dotted"
          >
            hepsini kaldır
          </button>
        </div>
      )}
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Otel ara..."
        className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-2 text-sm mb-1.5"
        data-testid={`${testid}-search`}
      />
      <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
        {filtered.length === 0 ? (
          <div className="p-3 text-center text-slate-500 text-sm">Aramanızla eşleşen otel yok.</div>
        ) : (
          filtered.map((h) => {
            const name = h.name || h;
            const isSel = selected.has(name);
            return (
              <label
                key={h.id || name}
                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition ${
                  isSel ? "bg-brand-orange/10 text-brand-orange-dark font-medium" : "hover:bg-canvas-cream/60 text-slate-700"
                }`}
                data-testid={`${testid}-option-${name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggle(name)}
                  className="rounded"
                />
                <span className="flex-1 truncate">{name}</span>
                {isSel && <span className="text-[10px] uppercase tracking-widest text-brand-orange">seçili</span>}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

